import React, { useEffect, useMemo, useRef, useState } from "react";
import AnnouncementBar from "../MarketingPage/AnnouncementBar";
import Header from "../MarketingPage/Header";
import Footer from "../MarketingPage/Footer";
import ProductSection from "../BestSellingProductListPage/ProductSection";
import NewArrivalSection from "../BestSellingProductListPage/NewArrivalSection";
import IconWithTextSection from "../BestSellingProductListPage/IconWithTextSection";
import HeroBannerCarousel from "./HeroBannerCarousel";
import { LandingPageRoot } from "./styles";
import { useGetProductsQuery, useGetPublicCollectionQuery } from "api/productApi";
import Loader from "shared/Loader";
import { labelForCategorySlug } from "utils/productCategory";
import {
  useCategorySectionScroll,
  useStableCatalogProducts,
} from "hooks/useCategoryCatalog";

const LandingPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [categorySlug, setCategorySlug] = useState("all");
  const categoriesSectionRef = useRef(null);
  const newArrivalsRef = useRef(null);

  const isAllCategories = categorySlug === "all";

  const { data: allProductsData, isLoading: isLoadingAll } = useGetProductsQuery(
    {
      page: currentPage,
      limit: pageSize,
    },
    { skip: !isAllCategories }
  );

  const { data: collectionData, isLoading: isLoadingCollection } =
    useGetPublicCollectionQuery(
      {
        slug: categorySlug,
        page: currentPage,
        limit: pageSize,
      },
      { skip: isAllCategories }
    );

  const handlePageChange = (page, size) => {
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
    } else {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [categorySlug]);

  const products = isAllCategories ? allProductsData?.products : collectionData?.products;
  const total = isAllCategories ? allProductsData?.total : collectionData?.total;
  const isLoading = isLoadingAll || isLoadingCollection;
  const { displayProducts } = useStableCatalogProducts(products, isLoading);
  const { createCategoryChangeHandler, reanchorAfterLoad } =
    useCategorySectionScroll(newArrivalsRef);
  const handleCategoryChange = useMemo(
    () => createCategoryChangeHandler(setCategorySlug, setCurrentPage),
    [createCategoryChangeHandler]
  );

  useEffect(() => {
    reanchorAfterLoad(isLoading);
  }, [isLoading, categorySlug, reanchorAfterLoad]);

  const sectionHeading =
    categorySlug === "all"
      ? "Latest Products"
      : `Latest Products — ${labelForCategorySlug(categorySlug)}`;

  const handleViewAllCategories = () => {
    setCategorySlug("all");
    setCurrentPage(1);
    requestAnimationFrame(() => {
      categoriesSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <LandingPageRoot>
      <AnnouncementBar />
      <Header />
      <HeroBannerCarousel onViewAllCategories={handleViewAllCategories} />
      <section ref={categoriesSectionRef}>
        <ProductSection
          heading={sectionHeading}
          products={displayProducts}
          currentPage={currentPage}
          pageSize={pageSize}
          total={total}
          onPageChange={handlePageChange}
        />
        <NewArrivalSection
          sectionRef={newArrivalsRef}
          products={displayProducts}
          categorySlug={categorySlug}
          onCategoryChange={handleCategoryChange}
          total={total}
          isLoading={isLoading}
        />
      </section>
      <IconWithTextSection />
      <Footer />
      <Loader isLoading={isLoading && displayProducts.length === 0} />
    </LandingPageRoot>
  );
};

export default LandingPage;
